import { useEffect, useRef, useState } from "react";

interface AnimatedCounterProps {
  value: number;
  duration?: number;
  prefix?: string;
  suffix?: string;
  decimals?: number;
  className?: string;
}

export function AnimatedCounter({
  value,
  duration = 2000,
  prefix = "",
  suffix = "",
  decimals = 0,
  className = "",
}: AnimatedCounterProps) {
  const [displayValue, setDisplayValue] = useState(0);
  const [hasAnimated, setHasAnimated] = useState(false);
  const [isVisible, setIsVisible] = useState(false);
  const ref = useRef<HTMLSpanElement>(null);
  const prevValueRef = useRef(0);

  // Track visibility
  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            setIsVisible(true);
          }
        });
      },
      { threshold: 0.1 }
    );

    if (ref.current) {
      observer.observe(ref.current);
    }

    return () => observer.disconnect();
  }, []);

  // Animate when visible AND value is valid (> 0)
  useEffect(() => {
    if (isVisible && value > 0 && !hasAnimated) {
      setHasAnimated(true);
      animateValue(0, value, duration);
      prevValueRef.current = value;
    } else if (hasAnimated && value !== prevValueRef.current && value > 0) {
      // Re-animate when value changes after initial animation
      animateValue(displayValue, value, duration / 2);
      prevValueRef.current = value;
    }
  }, [isVisible, value, hasAnimated, duration]);

  const animateValue = (start: number, end: number, animDuration: number) => {
    const startTime = performance.now();
    
    const easeOutQuart = (t: number): number => {
      return 1 - Math.pow(1 - t, 4);
    };

    const animate = (currentTime: number) => {
      const elapsed = currentTime - startTime;
      const progress = Math.min(elapsed / animDuration, 1);
      const easedProgress = easeOutQuart(progress);
      const currentValue = start + (end - start) * easedProgress;
      
      setDisplayValue(currentValue);

      if (progress < 1) {
        requestAnimationFrame(animate);
      }
    };

    requestAnimationFrame(animate);
  };

  const formatValue = (val: number): string => {
    if (decimals > 0) {
      return val.toFixed(decimals);
    }
    return Math.round(val).toLocaleString();
  };

  return (
    <span ref={ref} className={className}>
      {prefix}{formatValue(displayValue)}{suffix}
    </span>
  );
}

// Specialized version for currency
export function AnimatedCurrency({
  value,
  duration = 2000,
  className = "",
}: {
  value: number;
  duration?: number;
  className?: string;
}) {
  const [displayValue, setDisplayValue] = useState(0);
  const [hasAnimated, setHasAnimated] = useState(false);
  const [isVisible, setIsVisible] = useState(false);
  const ref = useRef<HTMLSpanElement>(null);
  const prevValueRef = useRef(0);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            setIsVisible(true);
          }
        });
      },
      { threshold: 0.1 }
    );

    if (ref.current) {
      observer.observe(ref.current);
    }

    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    if (isVisible && value > 0 && !hasAnimated) {
      setHasAnimated(true);
      animateValue(0, value, duration);
      prevValueRef.current = value;
    } else if (hasAnimated && value !== prevValueRef.current && value > 0) {
      animateValue(displayValue, value, duration / 2);
      prevValueRef.current = value;
    }
  }, [isVisible, value, hasAnimated, duration]);

  const animateValue = (start: number, end: number, animDuration: number) => {
    const startTime = performance.now();
    
    const easeOutQuart = (t: number): number => {
      return 1 - Math.pow(1 - t, 4);
    };

    const animate = (currentTime: number) => {
      const elapsed = currentTime - startTime;
      const progress = Math.min(elapsed / animDuration, 1);
      const easedProgress = easeOutQuart(progress);
      const currentValue = start + (end - start) * easedProgress;
      
      setDisplayValue(currentValue);

      if (progress < 1) {
        requestAnimationFrame(animate);
      }
    };

    requestAnimationFrame(animate);
  };

  const formatCurrency = (val: number): string => {
    if (val >= 1000000) {
      return `$${(val / 1000000).toFixed(2)}M`;
    } else if (val >= 1000) {
      return `$${Math.round(val).toLocaleString()}`;
    }
    return `$${Math.round(val).toLocaleString()}`;
  };

  return (
    <span ref={ref} className={className}>
      {formatCurrency(displayValue)}
    </span>
  );
}

// Specialized version for percentages
export function AnimatedPercentage({
  value,
  duration = 2000,
  decimals = 2,
  className = "",
}: {
  value: number;
  duration?: number;
  decimals?: number;
  className?: string;
}) {
  const [displayValue, setDisplayValue] = useState(0);
  const [hasAnimated, setHasAnimated] = useState(false);
  const [isVisible, setIsVisible] = useState(false);
  const ref = useRef<HTMLSpanElement>(null);
  const prevValueRef = useRef(0);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            setIsVisible(true);
          }
        });
      },
      { threshold: 0.1 }
    );

    if (ref.current) {
      observer.observe(ref.current);
    }

    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    // For percentages, animate even if value is 0 (but only after data loads)
    if (isVisible && !hasAnimated && (value > 0 || prevValueRef.current !== value)) {
      setHasAnimated(true);
      animateValue(0, value, duration);
      prevValueRef.current = value;
    } else if (hasAnimated && value !== prevValueRef.current) {
      animateValue(displayValue, value, duration / 2);
      prevValueRef.current = value;
    }
  }, [isVisible, value, hasAnimated, duration]);

  const animateValue = (start: number, end: number, animDuration: number) => {
    const startTime = performance.now();
    
    const easeOutQuart = (t: number): number => {
      return 1 - Math.pow(1 - t, 4);
    };

    const animate = (currentTime: number) => {
      const elapsed = currentTime - startTime;
      const progress = Math.min(elapsed / animDuration, 1);
      const easedProgress = easeOutQuart(progress);
      const currentValue = start + (end - start) * easedProgress;
      
      setDisplayValue(currentValue);

      if (progress < 1) {
        requestAnimationFrame(animate);
      }
    };

    requestAnimationFrame(animate);
  };

  return (
    <span ref={ref} className={className}>
      {displayValue.toFixed(decimals)}%
    </span>
  );
}
